import {Component, DestroyRef, inject, OnInit} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import Big from "big.js";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {filter, take} from "rxjs";
import {DealFxAnalysisActions} from "../../store/deal.actions";
import {FxActions} from "../../../shared/store/shared.actions";
import {selectDealFxAnalysis} from "../../store/deal/deal.selectors";
import {selectFxAutofillRate, selectFxCurrencies, selectFxHistoricalRate} from "../../../shared/store/fx/fx.selectors";
import {CurrencyOption} from "../../../shared/model/fx/currency-option";
import {DateUtil} from "../../../shared/utils/date-util";
import {
    AssetCurrencyAssignment,
    AssetCurrencyRow,
    AssetFxRateAssignment,
    DealFxAnalysis,
    FundCurrencyAssignment,
    FundCurrencyRow,
    FundFxRateAssignment
} from "../../models/deal-fx-analysis";

type MemberKind = "fund" | "asset";

// Mirrors the inline fund exchange-rate field's numberNotBiggerThan190000m() upper bound.
const MAX_MANUAL_FX_RATE = 190000;

interface FxMember {
    kind: MemberKind;
    id: number;            // sellerPositionId (fund) or assetValuationId (asset) — the apply key
    entityId: number;      // fundId (fund) or assetId (asset) — the currency-assignment key
    fundValuationId: number; // owning fund valuation — links a fund to its assets (for currency propagation)
    name: string;
    fundName?: string;     // owning fund (assets only)
    from?: string;         // source currency; undefined => missing currency
    to: string;            // target currency (deal ccy for funds, fund ccy for assets)
    current: number | null;
    source: string | null;
    stale: boolean;
    ccySet: boolean;       // a pending currency assignment chosen in this dialog
}

interface PairRuntime {
    from: string;
    to: string;
    latest: number | null;
    latestAsOf: string | null;
    priceable: boolean | null;   // null = latest not resolved yet
    historical: number | null | "NA";
    historicalAsOf: string | null;
    staged: boolean;
    stagedVal: number | null;
    basis: string | null;   // string => "latest", "manual" or a chosen date
    manual: number | null;
}

export interface FxPairGroup {
    key: string;
    from: string;
    to: string;
    same: boolean;
    unpriceable: boolean;
    inconsistent: boolean;
    members: FxMember[];
    runtime: PairRuntime;
}

@Component({
    selector: "valumize-deal-fx-rates-dialog",
    templateUrl: "./deal-fx-rates-dialog.component.html",
    styleUrls: ["./deal-fx-rates-dialog.component.scss"],
    standalone: false
})
export class DealFxRatesDialogComponent implements OnInit {

    private readonly store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef = inject(MatDialogRef<DealFxRatesDialogComponent>);

    readonly dealId: number = inject<{ dealId: number }>(MAT_DIALOG_DATA).dealId;

    dealCurrency?: string;
    members: FxMember[] = [];
    readonly runtimeByPair: Record<string, PairRuntime> = {};

    today = DateUtil.isoDate();
    chosenDate = this.today;
    isFetching = false;
    private pendingHistoricalFetches = 0;
    private fetchSeq = 0;   // generation token so stale historical-fetch callbacks can be ignored
    private readonly manualErrors: Record<string, string> = {};   // per-pair validation message for manual rate entry
    applyBasis: "latest" | "chosen" | null = null;

    currencyOptions: CurrencyOption[] = [];

    // Same-currency groups collapse to only the rows with issues (rate ≠ 1.0);
    // a key in this set means the user expanded that group to see all members.
    readonly expandedGroups = new Set<string>();

    ngOnInit(): void {
        this.store.dispatch(FxActions.loadCurrencyList());
        this.store.select(selectFxCurrencies)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((options) => (this.currencyOptions = options));

        this.store.dispatch(DealFxAnalysisActions.load({dealId: this.dealId}));
        this.store.select(selectDealFxAnalysis)
            .pipe(
                filter((slice) => slice.status === "LOADED"),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((slice) => this.onAnalysisLoaded(slice.data));
    }

    // -------------------------------------------------------------------------------- initialisation

    private onAnalysisLoaded(analysis: DealFxAnalysis): void {
        this.dealCurrency = analysis.dealCurrency;
        this.members = [
            ...analysis.fundRows.map((row) => this.toFundMember(row)),
            ...analysis.assetRows.map((row) => this.toAssetMember(row))
        ];
        this.uniquePairs().forEach((m) => this.ensurePair(m.from!, m.to));
        this.rebuildPairGroups();
        this.fetchLatestForAllPairs();
        this.autoFetch();
    }

    private toFundMember(row: FundCurrencyRow): FxMember {
        return {
            kind: "fund",
            id: row.sellerPositionId,
            entityId: row.fundId,
            fundValuationId: row.fundValuationId,
            name: row.fundName ?? "Fund",
            from: row.fundCurrencyMissing ? undefined : row.fundCurrency,
            to: this.dealCurrency ?? "",
            current: row.currentFxRateDeal ?? null,
            source: row.currentFxRateDealSource ?? null,
            stale: this.isStale(row.currentFxRateDealAsOfDate),
            ccySet: false
        };
    }

    private toAssetMember(row: AssetCurrencyRow): FxMember {
        return {
            kind: "asset",
            id: row.assetValuationId,
            entityId: row.assetId,
            fundValuationId: row.fundValuationId,
            name: row.assetName ?? "Asset",
            fundName: row.fundName,
            from: row.assetCurrencyMissing ? undefined : row.assetCurrency,
            to: row.fundCurrency ?? "",
            current: row.currentFxRateFund ?? null,
            source: row.currentFxRateFundSource ?? null,
            stale: this.isStale(row.currentFxRateFundAsOfDate),
            ccySet: false
        };
    }

    private isStale(asOfDate?: string): boolean {
        if (!asOfDate) {
            return false;
        }
        // Treat anything older than ~7 days as stale (informational only). Use DateUtil (Luxon) so the
        // day boundary is computed on semantic dates, not native Date's UTC-vs-local midnight.
        const asOf = DateUtil.fromIsoDate(asOfDate);
        const today = DateUtil.fromIsoDate(this.today);
        return !!asOf && !!today && today.diff(asOf, "days").days > 7;
    }

    // ----------------------------------------------------------------------------------- grouping

    private readonly hasOwnCcy = (m: FxMember): boolean => m.from != null && m.from !== "";
    // Groupable only with BOTH a source and a target currency. An asset whose owning fund has no
    // currency (to === "") must not form a pair or trigger an ECB fetch with an empty target symbol;
    // it stays blocked until the fund's currency is set (the fund itself shows in needsCurrencyMembers).
    private readonly hasCcy = (m: FxMember): boolean => this.hasOwnCcy(m) && m.to != null && m.to !== "";
    private readonly isSame = (m: FxMember): boolean => this.hasCcy(m) && m.from === m.to;
    private readonly pairKey = (m: FxMember): string => `${m.from}_${m.to}`;

    private uniquePairs(): FxMember[] {
        const seen = new Set<string>();
        return this.members.filter((m) => {
            if (!this.hasCcy(m)) {
                return false;
            }
            const key = this.pairKey(m);
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    get needsCurrencyMembers(): FxMember[] {
        return this.members.filter((m) => !this.hasOwnCcy(m));
    }

    // Memoised grouped view. Rebuilt only when its inputs change — the member set / a member's
    // from/to/current (onAnalysisLoaded, setMemberCurrency) or a pair's priceability (fetch callback).
    // Staging (staged/stagedVal/basis) and latest/historical are read live via each group's shared
    // `runtime` reference, so they need no rebuild. Rebuilding here (not in a getter) keeps it off the
    // change-detection hot path, where the template and stagedCounts/blockingErrors/canApply all read it.
    pairGroups: FxPairGroup[] = [];

    private rebuildPairGroups(): void {
        const groups = new Map<string, FxPairGroup>();
        this.members.filter((m) => this.hasCcy(m)).forEach((m) => {
            const key = this.pairKey(m);
            const runtime = this.runtimeByPair[key] ?? this.ensurePair(m.from!, m.to);
            let group = groups.get(key);
            if (!group) {
                group = {
                    key,
                    from: m.from!,
                    to: m.to,
                    same: this.isSame(m),
                    unpriceable: !this.isSame(m) && runtime.priceable === false,
                    inconsistent: false,
                    members: [],
                    runtime
                };
                groups.set(key, group);
            }
            group.members.push(m);
        });

        groups.forEach((group) => {
            group.unpriceable = !group.same && group.runtime.priceable === false;
            const stored = group.members
                .filter((m) => m.current != null && !this.isSame(m))
                .map((m) => (m.current as number).toFixed(4));
            group.inconsistent = new Set(stored).size > 1;
        });

        const rank = (g: FxPairGroup): number => (g.unpriceable ? 2 : g.same ? 1 : 0);
        this.pairGroups = [...groups.values()].sort((a, b) => rank(a) - rank(b) || a.key.localeCompare(b.key));
    }

    private ensurePair(from: string, to: string): PairRuntime {
        const key = `${from}_${to}`;
        if (!this.runtimeByPair[key]) {
            this.runtimeByPair[key] = {
                from,
                to,
                latest: from === to ? 1.0 : null,
                latestAsOf: null,
                priceable: from === to ? true : null,
                historical: from === to ? 1.0 : null,
                historicalAsOf: null,
                staged: false,
                stagedVal: null,
                basis: null,
                manual: null
            };
        }
        return this.runtimeByPair[key];
    }

    // --------------------------------------------------------------------------- latest / historical

    private fetchLatestForAllPairs(): void {
        const dispatched = new Set<string>();
        this.uniquePairs().forEach((m) => {
            const from = m.from!;
            const to = m.to;
            const runtime = this.ensurePair(from, to);
            if (from === to) {
                runtime.latest = 1.0;
                runtime.priceable = true;
                return;
            }
            if (runtime.priceable !== null) {
                return;   // already resolved — don't re-dispatch (disrupts the shared autofill slice) or re-subscribe
            }
            const key = `${from}_${to}`;
            if (!dispatched.has(key)) {
                this.store.dispatch(FxActions.fetchAutofillRate({from, to}));
                dispatched.add(key);
            }
            this.store.select(selectFxAutofillRate(from, to))
                .pipe(
                    filter((slice) => slice?.status === "LOADED" || slice?.status === "ERROR"),
                    take(1),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((slice) => {
                    if (slice?.status === "LOADED" && slice.data.fraction != null) {
                        runtime.latest = slice.data.fraction;
                        runtime.latestAsOf = slice.data.asOfDate;
                        runtime.priceable = true;
                    } else {
                        runtime.latest = null;
                        runtime.priceable = false;
                    }
                    this.rebuildPairGroups();   // priceability drives group.unpriceable + sort order
                });
        });
    }

    private autoFetch(): void {
        const pairs = this.uniquePairs().filter((m) => m.from !== m.to);
        if (pairs.length === 0) {
            this.isFetching = false;
            return;
        }
        this.isFetching = true;
        // Supersede any in-flight fetch from a prior date/currency change: late callbacks from an
        // older batch must not decrement this batch's counter or write stale rates.
        const seq = ++this.fetchSeq;
        this.pendingHistoricalFetches = 0;
        const date = this.chosenDate;
        const dispatched = new Set<string>();
        pairs.forEach((m) => {
            const from = m.from!;
            const to = m.to;
            const runtime = this.ensurePair(from, to);
            runtime.historical = null;
            const key = `${from}_${to}`;
            if (!dispatched.has(key)) {
                this.store.dispatch(FxActions.loadHistoricalExchangeRates({date, from, to}));
                dispatched.add(key);
                this.pendingHistoricalFetches++;
            }
            this.store.select(selectFxHistoricalRate(date, from, to))
                .pipe(
                    filter((slice) => slice?.status === "LOADED" || slice?.status === "ERROR"),
                    take(1),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((slice) => {
                    if (seq !== this.fetchSeq) {
                        return;   // superseded by a newer fetch — ignore this stale emission
                    }
                    if (slice?.status === "LOADED" && slice.data.fraction != null) {
                        runtime.historical = slice.data.fraction;
                        runtime.historicalAsOf = slice.data.asOfDate;
                    } else {
                        runtime.historical = "NA";
                    }
                    this.pendingHistoricalFetches--;
                    if (this.pendingHistoricalFetches <= 0) {
                        this.isFetching = false;
                        if (this.applyBasis === "chosen") {
                            this.stageAllAtChosen();
                        }
                    }
                });
        });
    }

    // ----------------------------------------------------------------------------- date handling

    onDateChange(date: string | null): void {
        this.chosenDate = date || this.today;
        this.clearChosenStaging();
        this.autoFetch();
    }

    useToday(): void {
        this.onDateChange(this.today);
    }

    private clearChosenStaging(): void {
        Object.values(this.runtimeByPair).forEach((p) => {
            if (p.basis !== "manual" && p.from !== p.to) {
                p.staged = false;
                p.stagedVal = null;
                p.basis = null;
            }
        });
    }

    // ------------------------------------------------------------------------------ global staging

    private eligiblePairs(): PairRuntime[] {
        return Object.values(this.runtimeByPair).filter((p) => p.from !== p.to && p.priceable === true);
    }

    autoApplyLatest(): void {
        if (this.isFetching) {
            return;
        }
        this.applyBasis = "latest";
        this.eligiblePairs().forEach((p) => {
            if (p.latest != null) {
                p.staged = true;
                p.stagedVal = p.latest;
                p.basis = "latest";
            }
        });
    }

    applyAtChosen(): void {
        if (this.isFetching) {
            return;
        }
        this.applyBasis = "chosen";
        this.stageAllAtChosen();
    }

    private stageAllAtChosen(): void {
        this.eligiblePairs().forEach((p) => {
            if (p.historical == null || p.historical === "NA") {
                p.staged = false;
                p.stagedVal = null;
                p.basis = null;
                return;
            }
            p.staged = true;
            p.stagedVal = p.historical;
            p.basis = this.chosenDate;
        });
    }

    clearStaging(): void {
        this.applyBasis = null;
        Object.values(this.runtimeByPair).forEach((p) => {
            p.staged = false;
            p.stagedVal = null;
            p.basis = null;
        });
    }

    unstage(key: string): void {
        const p = this.runtimeByPair[key];
        if (!p) {
            return;
        }
        p.staged = false;
        p.stagedVal = null;
        p.basis = null;
        if (p.from !== p.to && p.priceable === false) {
            p.manual = null;
        }
    }

    setManual(key: string, raw: string | number | null): void {
        const p = this.runtimeByPair[key];
        if (!p) {
            return;
        }
        const value = typeof raw === "number" ? raw : parseFloat(raw ?? "");
        // Upper bound mirrors the inline fund exchange-rate field's numberNotBiggerThan190000m() validator.
        if (!isNaN(value) && value > 0 && value <= MAX_MANUAL_FX_RATE) {
            delete this.manualErrors[key];
            p.manual = value;
            p.staged = true;
            p.stagedVal = value;
            p.basis = "manual";
        } else {
            const isEmpty = raw == null || `${raw}`.trim() === "";
            if (isEmpty) {
                delete this.manualErrors[key];   // cleared, not invalid
            } else if (!isNaN(value) && value > MAX_MANUAL_FX_RATE) {
                this.manualErrors[key] = `Rate must be ≤ ${MAX_MANUAL_FX_RATE}.`;
            } else {
                this.manualErrors[key] = "Enter a rate greater than 0.";
            }
            p.manual = null;
            p.staged = false;
            p.stagedVal = null;
            p.basis = null;
        }
    }

    manualError(key: string): string | undefined {
        return this.manualErrors[key];
    }

    // ---------------------------------------------------------------------------- currency picker

    setMemberCurrency(member: FxMember, iso: string): void {
        if (!iso) {
            return;
        }
        member.from = iso;
        member.ccySet = true;
        member.current = iso === member.to ? 1.0 : null;
        member.source = iso === member.to ? "same currency" : null;
        // Setting a fund's currency also becomes the target currency of its assets (asset→fund pairs),
        // so their `to` (captured at load) must be refreshed or they stay grouped against a stale/empty ccy.
        if (member.kind === "fund") {
            this.members
                .filter((a) => a.kind === "asset" && a.fundValuationId === member.fundValuationId)
                .forEach((a) => (a.to = iso));
        }
        this.ensurePair(member.from, member.to);
        this.rebuildPairGroups();   // membership / from / to / current changed
        // fetchLatestForAllPairs dedupes by pair and seeds runtimes for any newly-formed pairs
        // (the fund's own pair and, for a fund change, its assets' pairs).
        this.fetchLatestForAllPairs();
        this.autoFetch();
    }

    // ------------------------------------------------------------------------------- counts / blocks

    // A curated palette so each currency pair gets a distinct, stable chip colour
    // (e.g. SEK → USD reads differently from EUR → USD). `same`/`unpriceable`
    // groups keep their SCSS colours (grey / red), so return null for those.
    private static readonly PAIR_CHIP_COLORS = [
        "#4949cb", // purple
        "#2c7a6e", // teal
        "#1d4ed8", // blue
        "#b7791f", // amber
        "#be185d", // pink
        "#0f766e", // deep teal
        "#7c3aed", // violet
        "#c2410c"  // burnt orange
    ];

    // Cache by pair key: the colour is a pure function of the immutable from/to, but this is called
    // per group on every change-detection cycle, so hash once and reuse.
    private readonly pairChipColorCache: Record<string, string> = {};

    pairChipColor(group: FxPairGroup): string | null {
        if (group.same || group.unpriceable) {
            return null;
        }
        const key = `${group.from}_${group.to}`;
        const cached = this.pairChipColorCache[key];
        if (cached) {
            return cached;
        }
        let hash = 0;
        for (let i = 0; i < key.length; i++) {
            hash = (hash * 31 + key.charCodeAt(i)) | 0;
        }
        const palette = DealFxRatesDialogComponent.PAIR_CHIP_COLORS;
        const color = palette[Math.abs(hash) % palette.length];
        this.pairChipColorCache[key] = color;
        return color;
    }

    pctDiff(current: number | null, latest: number | null): number | null {
        if (current == null || latest == null || current === 0) {
            return null;
        }
        return new Big(latest).minus(current).div(current).toNumber();
    }

    willWrite(member: FxMember, group: FxPairGroup): boolean {
        return this.isSame(member) ? member.current !== 1.0 : group.runtime.staged;
    }

    // A same-currency member whose stored rate isn't 1.0000 — an error that Apply corrects.
    private readonly sameIssue = (member: FxMember): boolean =>
        this.isSame(member) && member.current != null && member.current !== 1.0;

    // Same-currency groups show only issue rows by default; other groups show all members.
    visibleMembers(group: FxPairGroup): FxMember[] {
        if (!group.same || this.expandedGroups.has(group.key)) {
            return group.members;
        }
        return group.members.filter((m) => this.sameIssue(m));
    }

    hiddenMemberCount(group: FxPairGroup): number {
        if (!group.same || this.expandedGroups.has(group.key)) {
            return 0;
        }
        return group.members.filter((m) => !this.sameIssue(m)).length;
    }

    isExpanded(key: string): boolean {
        return this.expandedGroups.has(key);
    }

    toggleGroup(key: string): void {
        if (this.expandedGroups.has(key)) {
            this.expandedGroups.delete(key);
        } else {
            this.expandedGroups.add(key);
        }
    }

    // Count of same-currency rate errors across all pairs — surfaced in the issues banner.
    get sameCurrencyIssues(): number {
        return this.members.filter((m) => this.sameIssue(m)).length;
    }

    get stagedCounts(): { fundRows: number; assetRows: number; total: number; ccyChanges: number } {
        let fundRows = 0;
        let assetRows = 0;
        this.pairGroups.forEach((group) => {
            group.members.forEach((m) => {
                if (this.willWrite(m, group)) {
                    if (m.kind === "fund") {
                        fundRows++;
                    } else {
                        assetRows++;
                    }
                }
            });
        });
        const ccyChanges = this.members.filter((m) => m.ccySet).length;
        return {fundRows, assetRows, total: fundRows + assetRows, ccyChanges};
    }

    get blockingErrors(): { missingCcy: number; unpriceable: number; total: number } {
        const missingCcy = this.needsCurrencyMembers.length;
        let unpriceable = 0;
        this.pairGroups.forEach((group) => {
            if (group.unpriceable && !group.runtime.staged) {
                unpriceable += group.members.length;
            }
        });
        return {missingCcy, unpriceable, total: missingCcy + unpriceable};
    }

    get canApply(): boolean {
        const {total, ccyChanges} = this.stagedCounts;
        return (total > 0 || ccyChanges > 0) && !this.isFetching && this.blockingErrors.total === 0;
    }

    get applyButtonLabel(): string {
        const {total, ccyChanges} = this.stagedCounts;
        const rates = `Apply ${total} ${total === 1 ? "Rate" : "Rates"}`;
        return ccyChanges > 0 ? `${rates} + ${ccyChanges} ccy` : rates;
    }

    get basisLabel(): string {
        if (this.applyBasis === "latest") {
            return "Basis: latest";
        }
        if (this.applyBasis === "chosen") {
            return `Basis: @ ${this.chosenDate}`;
        }
        return "";
    }

    // ----------------------------------------------------------------------------------------- apply

    apply(): void {
        if (!this.canApply) {
            return;
        }
        const fundRates: FundFxRateAssignment[] = [];
        const assetRates: AssetFxRateAssignment[] = [];

        this.pairGroups.forEach((group) => {
            group.members.forEach((m) => {
                if (!this.willWrite(m, group)) {
                    return;
                }
                const isSame = this.isSame(m);
                const fraction = isSame ? 1.0 : (group.runtime.stagedVal as number);
                const source = isSame || group.runtime.basis === "manual"
                    ? "user"
                    : group.runtime.basis === "latest"
                        ? `ECB ${group.runtime.latestAsOf ?? this.today}`
                        : `ECB ${group.runtime.historicalAsOf ?? group.runtime.basis}`;
                if (m.kind === "fund") {
                    fundRates.push({sellerPositionId: m.id, fraction, source});
                } else {
                    assetRates.push({assetValuationId: m.id, fxRateFundFraction: fraction, source});
                }
            });
        });

        const fundCurrencies: FundCurrencyAssignment[] = this.members
            .filter((m) => m.ccySet && m.kind === "fund" && m.from)
            .map((m) => ({fundId: m.entityId, currencyIso: m.from as string}));
        const assetCurrencies: AssetCurrencyAssignment[] = this.members
            .filter((m) => m.ccySet && m.kind === "asset" && m.from)
            .map((m) => ({assetId: m.entityId, currencyIso: m.from as string}));

        this.store.dispatch(DealFxAnalysisActions.applyRates({
            dealId: this.dealId,
            fundRates,
            assetRates,
            fundCurrencies,
            assetCurrencies
        }));
        this.dialogRef.close();
    }

    close(): void {
        this.dialogRef.close();
    }
}
